import { useState, useEffect, useSyncExternalStore, type ReactNode } from "react";
import {
  writeShowHolidays,
  subscribeShowHolidays,
  getShowHolidaysSnapshot,
} from "@/lib/showHolidaysPref";
import { blurOnEnter } from "@/lib/blurOnEnter";
import { Children, isValidElement } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, Pencil, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { useIsPro } from "@/hooks/useIsPro";
import { useKinInboxAccess } from "@/hooks/useKinInboxAccess";
import { useMomTypes } from "@/hooks/useMomTypes";
import { usePreferences } from "@/hooks/useUserPreferences";
import { resolveSavedZip } from "@/lib/userLocation";
import { useFavorites } from "@/hooks/useFavorites";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { useAppleCalendar } from "@/hooks/useAppleCalendar";
import { useKroger } from "@/hooks/useKroger";
import { usePinterest } from "@/hooks/usePinterest";
import { useAppSetting } from "@/hooks/useAppSetting";
import { APP_SETTING_KEYS } from "@/hooks/appSettingKeys";
import { isNotificationsAvailable } from "@/lib/notifications";
import { DELIVERY_FEATURES, isRowOn } from "@/lib/deliveryChannel";
import { supabase } from "@/integrations/supabase/client";
import { clearSupabaseStorage, setDeleting as setDeletingFlag } from "@/lib/authCleanup";
import { getFirstName } from "@/lib/userDisplayName";
import { trackFeature } from "@/lib/analytics";
import { openExternal } from "@/lib/openExternal";
import {
  EXTERNAL_SUPABASE_URL,
  EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
} from "@/integrations/external-supabase/client";
import { MOM_TYPES, findChipByLabel } from "@/constants/chipPools";
import {
  openProSetupFlow,
  clearProSetupDone,
  isProSetupDone,
  useProSetupFlowOpen,
} from "@/lib/proSetupFlow";
import MomTypeSelector from "@/components/MomTypeSelector";
import KinAvatar from "@/components/KinAvatar";
import MyKidsSection from "@/components/MyKidsSection";
import FamilyProfileSection from "@/components/FamilyProfileSection";
import HowKinReachesYou from "@/components/HowKinReachesYou";
import BriefingBuilder from "@/components/BriefingBuilder";
import { BRIEFING_ADDON_KEYS, isBriefingAddonOn } from "@/lib/briefingAddons";
import AppleCalendarConnect from "@/components/AppleCalendarConnect";
import { KinCalendarFeedPanel } from "@/components/KinCalendarFeedConnect";
import { useKinCalendarFeed } from "@/hooks/useKinCalendarFeed";
import EmailForwardingSheet from "@/components/EmailForwardingSheet";
import BottomSheet from "@/components/shared/BottomSheet";
import KinFAQ, { FAQ_ITEMS } from "@/components/KinFAQ";
import GetHelp from "@/components/GetHelp";
import KinProBadge from "@/components/KinProBadge";
import { useOpenPro } from "@/components/ProUpsell";
import { FREE_TIER_FACT_CATEGORY, FREE_TIER_FACT_KEYS } from "@/lib/familyProfileTier";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FREE_INBOX_MONTHLY_CAP = 3;

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session, signOut, proLoaded, adminLoaded, adminPreview } = useAuth();
  const isAdmin = useAdmin();
  // useIsPro(), NOT useAuth().isPro. Aug 5 2026: useAuth reads the entitlements
  // row once per sign-in (useAuth.tsx:104-126, deps [user?.id, loading]) and
  // never again, so after a mid-session upgrade its isPro stays false forever.
  // EntitlementFlipListener publishes the flip to an override store instead
  // (entitlementFlip.ts:8-12 — useAuth is a locked file and cannot be written
  // to), and useIsPro folds that override in. Reading the raw value here left
  // Pro-only Settings rows hidden and the Inbox line on free wording for a mom who
  // had already paid. This is the same binding BriefingBuilder uses, so the
  // Morning Notes summary count still matches what that component renders.
  const isPro = useIsPro();
  const isProTier = isPro || (isAdmin && adminPreview);
  const openPro = useOpenPro();
  // ── Pro setup row (Aug 5 2026) ───────────────────────────────────────────
  //
  // This row used to be an admin/test-cohort testing harness, gated
  // `isPro && (isAdmin || isTestCohort)` — hence the useEntitlement/'comp'
  // source read that used to live here, now removed with its last consumer.
  // It is now the supported path to the Pro setup wizard for EVERY Pro
  // account, because the automatic trigger has
  // missed repeatedly on the Median shell (#623, #627, and a service worker
  // serving a stale bundle on top of both). A mom who has paid needs a way to
  // reach the thing she paid for that does not depend on any of that working.
  //
  // Subscribing to the open-store is what makes the done-flag re-read when the
  // wizard closes — localStorage alone would leave the row still saying
  // "Finish setting up" until some unrelated re-render. While the wizard is
  // open the setup genuinely is not done, so this reads correctly both ways.
  const proSetupOpen = useProSetupFlowOpen();
  const proSetupDone = !proSetupOpen && isProSetupDone();
  const { momTypes, refetch: refetchMomTypes } = useMomTypes();
  const { prefs, loading: prefsLoading, updatePreferences, refetchPreferences } = usePreferences();
  const { favorites, removeFavorite } = useFavorites();
  const { connected: gcalConnected, checking: gcalChecking, connect: gcalConnect, disconnect: gcalDisconnect } = useGoogleCalendar();
  const { connected: appleCalConnected, calendars: appleCalendars } = useAppleCalendar();
  // #1647 — the OTHER direction (Kin → Apple). Both directions share one row,
  // and the row summary and the panel share ONE hook instance (this one), so
  // enable()/disable() in the panel updates the header in the same render.
  const kinFeed = useKinCalendarFeed();
  const kinFeedActive = kinFeed.active;
  const appleRowValue = [
    kinFeedActive ? "Showing ✓" : null,
    appleCalConnected ? `Reading ✓ (${appleCalendars.length})` : null,
  ].filter(Boolean).join(" · ") || "Not connected";
  const { connected: krogerConnected, checking: krogerChecking, connect: krogerConnect, disconnect: krogerDisconnect } = useKroger();
  // #1073 — Pinterest boards for party inspo.
  const { connected: pinterestConnected, stored: pinterestStored, checking: pinterestChecking, connect: pinterestConnect, disconnect: pinterestDisconnect } = usePinterest();
  const [confirmDisconnectPinterest, setConfirmDisconnectPinterest] = useState(false);
  const pushAvailable = isNotificationsAvailable();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showMomSelector, setShowMomSelector] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  // #740 — separate from confirmDisconnect (gcal-specific) so disconnecting
  // one provider's dialog can't accidentally fire for the other.
  const [confirmDisconnectKroger, setConfirmDisconnectKroger] = useState(false);
  const [krogerAutoloadToggling, setKrogerAutoloadToggling] = useState(false);
  const [replayingTour, setReplayingTour] = useState(false);
  const [replayingProSetup, setReplayingProSetup] = useState(false);
  // Kin Inbox isn't publicly launched — full access is admins + the tester
  // allowlist (useKinInboxAccess); everyone else sees the locked Kin Pro
  // teaser below instead of the working email-forwarding row.
  const { hasAccess: hasInboxAccess, loaded: inboxAccessLoaded } = useKinInboxAccess();
  const [inboxProSheetOpen, setInboxProSheetOpen] = useState(false);
  const [inboxAddress, setInboxAddress] = useState<string | null>(null);
  const [inboxUsageCount, setInboxUsageCount] = useState<number | null>(null);
  const [settingUpInbox, setSettingUpInbox] = useState(false);
  const [showInboxSheet, setShowInboxSheet] = useState(false);
  const [inboxSheetAllowDisconnect, setInboxSheetAllowDisconnect] = useState(false);
  const [zipInput, setZipInput] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const currentFirstName = getFirstName(user);

  // Phase A summary-row layout: exactly one row expanded at a time across the
  // whole page. Tap the open row again (or a different row) to collapse it.
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const toggleRow = (id: string) => setExpandedRow((cur) => (cur === id ? null : id));

  // Holidays — read through the store rather than useState, so the Today tab (mounted
  // at the same time inside Index.tsx) re-renders the moment this flips. A
  // useState here would leave the calendar showing holidays until a reload.
  const showHolidays = useSyncExternalStore(
    subscribeShowHolidays,
    getShowHolidaysSnapshot,
    getShowHolidaysSnapshot,
  );

  // FAQ moved from an inline section into its own bottom sheet (Phase A).
  const [faqSheetOpen, setFaqSheetOpen] = useState(false);

  // Seeded on mount by the count query below, then kept live by
  // FamilyProfileSection's onFactCountChange once that section is open.
  // null means "not loaded yet", not "zero facts".
  const [factCount, setFactCount] = useState<number | null>(null);

  // #1211 — SummaryRow renders its children ONLY while expanded, so
  // FamilyProfileSection (the thing that reports the count upward) does not
  // exist until she taps the row open. "What Kin's learned" therefore read
  // "Getting started" on every fresh load of this page no matter how many
  // facts were stored — a bug independent of #1210, and still there once that
  // one is fixed.
  //
  // head: true means the count arrives in the Content-Range header and NO rows
  // are transferred. Do not let this grow into a row fetch that reads .length
  // off the result: this runs on every Settings mount, for free and Pro alike.
  //
  // Column names: this query deliberately names only `id`, `user_id` and
  // `archived`. family_context has no `created_at` (its creation timestamp is
  // `learned_at`) — see #1210, which was that exact mistake on this table.
  //
  // ⚠️ #763 — THIS SEED COUNTS WHAT SHE CAN SEE, not what is stored.
  //
  // Found while addressing a Copilot finding about the live updater, and it is
  // the bigger half of the same defect: this query runs on Settings mount, so
  // the collapsed row is drawn from it BEFORE FamilyProfileSection ever
  // mounts. Unfiltered, a free account whose facts are all Pro-collected read
  // "12 things" on a row that opens onto a locked upsell card — a number
  // contradicting the screen it labels, which is what #1210 was.
  //
  // The filter is DERIVED from familyProfileTier's constants rather than
  // written out as "kid"/"birthdate" here. Two copies of the carve-out, one in
  // a component and one in a query, is how the label and the list drift apart.
  //
  // Waits for the tier (`proLoaded && adminLoaded`) for the same reason the
  // hub does: seeding early would publish the free count to a Pro account and
  // then correct it.
  useEffect(() => {
    if (!user || !proLoaded || !adminLoaded) return;
    let cancelled = false;
    (async () => {
      let query = supabase
        .from("family_context")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("archived", false);
      if (!isProTier) {
        query = query
          .eq("category", FREE_TIER_FACT_CATEGORY)
          .in("key", [...FREE_TIER_FACT_KEYS]);
      }
      const { count, error } = await query;
      if (cancelled) return;
      if (error) {
        // Leave factCount null so the row keeps saying "Getting started".
        // Never a fabricated number, never a crash — and console.error rather
        // than warn, because warn does not reach Sentry (#1227).
        console.error("[settings] family_context count failed", error);
        return;
      }
      // Seed only. If FamilyProfileSection has already reported a live count
      // (she opened the row before this resolved), that value is fresher than
      // this one and must win.
      setFactCount((prev) => (prev === null ? count ?? 0 : prev));
    })();
    return () => {
      cancelled = true;
    };
  }, [user, proLoaded, adminLoaded, isProTier]);

  // Deep-link support: `navigate("/settings#some-id")` scrolls that section
  // into view and briefly highlights it (e.g. Trip Planner's empty state
  // sends users to #email-forwarding-row). React Router doesn't do native
  // browser hash-scrolling on client-side navigation, so this does it by hand.
  useEffect(() => {
    const id = location.hash?.slice(1);
    if (!id) return;
    // #1291 — same rule as the row's own onToggle: no Inbox access, no setup
    // button. Trip Planner (which sends this link) is gated on access too, but
    // a hash in a bookmarked or pasted URL is not.
    //
    // ⚠️ Waits for access to LOAD. `hasInboxAccess` reads false while the
    // entitlement row is in flight, so deciding early would hand a paying mom
    // the Pro sheet on a cold open. The effect re-runs once it has loaded.
    if (id === "email-forwarding-row") {
      if (!inboxAccessLoaded) return;
      if (hasInboxAccess) setExpandedRow("email");
      else setInboxProSheetOpen(true);
    }
    if (id === "learned-row") setExpandedRow("learned");
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedId(id);
    });
    const timeout = setTimeout(() => setHighlightedId(null), 2200);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [location.hash, hasInboxAccess, inboxAccessLoaded]);

  // #667 (and #666) — `navigate("/settings?editFact=<id>#learned-row")` both
  // expands "What Kin's learned" (above) and hands FamilyProfileSection the
  // one fact to auto-open for editing.
  const targetFactId = new URLSearchParams(location.search).get("editFact") || undefined;

  // Pre-fill both controls from saved preferences once they load.
  useEffect(() => {
    if (prefsLoading) return;
    // resolveSavedZip, not prefs.zip_code — a mom who only ever filled in the
    // Morning Notes weather zip has told Kin where she lives, and this row
    // showing "Add it" underneath that was the second half of #533. Blurring
    // the field promotes it into the canonical column; nothing writes back to
    // briefing_prefs, so weather's cached lat/lon are untouched.
    setZipInput(resolveSavedZip(prefs) ?? "");
  }, [prefsLoading, prefs.zip_code, prefs.briefing_prefs]);

  // Persist zip on blur — 5 digits or empty are both valid; anything else is ignored.
  const handleZipBlur = () => {
    const trimmed = zipInput.trim();
    if (trimmed !== "" && !/^\d{5}$/.test(trimmed)) return;
    const next = trimmed === "" ? null : trimmed;
    if (next === (prefs.zip_code ?? null)) return;
    updatePreferences({ zip_code: next });
  };


  // #742 — overnight Kroger cart auto-load toggle. House rule 16: optimistic
  // write via updatePreferences, and on a returned error roll back just this
  // field (not the whole row) and toast — same shape as #743's grocery
  // autofill toggle. A real cart mutation is opt-in and every failed write
  // must actually be caught, not just claimed.
  const handleToggleKrogerAutoload = async (nextValue: boolean) => {
    setKrogerAutoloadToggling(true);
    const error = await updatePreferences({ kroger_cart_autoload_enabled: nextValue });
    setKrogerAutoloadToggling(false);
    if (error) {
      console.error("[Settings] Kroger autoload toggle write failed:", error.message);
      await updatePreferences({ kroger_cart_autoload_enabled: !nextValue }, { write: false });
      toast.error("Couldn't save that — try again in a sec 💜");
      return;
    }
    toast.success(
      nextValue
        ? "Auto-load turned on — kicks in after your next meal plan."
        : "Auto-load turned off. Your Kroger cart won't be touched automatically."
    );
  };

  // Monthly forwarded-email count for the "X of 3 imports" / "you've
  // forwarded X emails" copy. status='parsed' is the same "delivered value"
  // signal kin-inbox's free-tier cap enforces on (see capLogic.ts's
  // isOverFreeTierCap and getMonthlyParsedCount in
  // supabase/functions/kin-inbox/index.ts) — keep this filter and the UTC
  // month boundary in sync with that enforcement so the number she sees
  // here always matches what's actually capped.
  const refreshInboxUsageCount = async (userId: string) => {
    const now = new Date();
    const startOfMonthUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0));
    const { count, error } = await supabase
      .from("inbox_emails")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("status", "parsed")
      .gte("created_at", startOfMonthUtc.toISOString());
    if (error) { console.error("refreshInboxUsageCount failed:", error); return; }
    setInboxUsageCount(count ?? 0);
  };

  useEffect(() => {
    if (!hasInboxAccess || !user) return;
    let cancelled = false;
    (async () => {
      try {
        // Lookup only — do NOT auto-create. The row should read as
        // "not connected" until the user explicitly taps "Set up".
        const { data: addrRow } = await supabase
          .from("inbox_addresses")
          .select("address")
          .eq("user_id", user.id)
          .maybeSingle();
        if (!cancelled && addrRow?.address) {
          setInboxAddress(addrRow.address);
          await refreshInboxUsageCount(user.id);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user, hasInboxAccess]);

  const handleSetUpInbox = async () => {
    // #1291 — Inbox is Pro. The button is only reachable with access, but the
    // handler checks too, so no future path into this row can create an
    // address for a free account.
    if (!user || settingUpInbox || !hasInboxAccess) return;
    setSettingUpInbox(true);
    try {
      const { data: addr, error } = await (supabase as any).rpc("get_or_create_inbox_address", { p_user_id: user.id });
      if (error || !addr) throw error ?? new Error("no address returned");
      setInboxAddress(String(addr));
      setInboxUsageCount(null);
      void refreshInboxUsageCount(user.id);
      trackFeature("kin_inbox_setup");
      setInboxSheetAllowDisconnect(false);
      setShowInboxSheet(true);
    } catch {
      toast.error("Couldn't set that up — try again in a bit");
    } finally {
      setSettingUpInbox(false);
    }
  };

  const handleManageInbox = () => {
    setInboxSheetAllowDisconnect(true);
    setShowInboxSheet(true);
  };

  const handleDisconnectInbox = async () => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from("inbox_addresses")
        .delete()
        .eq("user_id", user.id);
      if (error) throw error;
      setInboxAddress(null);
      setInboxUsageCount(null);
      trackFeature("kin_inbox_disconnect");
      toast("Disconnected 💜");
    } catch {
      toast.error("Couldn't disconnect — try again in a bit");
    } finally {
      setShowInboxSheet(false);
    }
  };

  const handleReplayTour = async () => {
    if (!user || replayingTour) return;
    setReplayingTour(true);
    try {
      const { error } = await supabase
        .from("user_preferences")
        .upsert(
          {
            user_id: user.id,
            onboarding_complete: false,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );
      if (error) throw error;
      refetchPreferences();
      toast("Replaying welcome tour 💜", {
        style: {
          background: "#FFFFFF",
          border: "1.5px solid #5050C8",
          fontFamily: "'DM Serif Display', serif",
          color: "#1C1A3E",
        },
        duration: 1500,
      });
      setTimeout(() => navigate("/"), 400);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't replay the tour. Try again!");
      setReplayingTour(false);
    }
  };

  // Opens the Pro setup wizard for any Pro account (was admins + test cohort
  // until Aug 5 2026 — see the note by proSetupDone above). clearProSetupDone()
  // runs either way and is a no-op for a mom who never finished it. Mirrors
  // handleReplayTour's toast + delayed navigate pattern; unlike the tour
  // (DB-driven via onboarding_complete), the Pro setup modal opens directly.
  const handleReplayProSetup = () => {
    if (replayingProSetup) return;
    setReplayingProSetup(true);
    clearProSetupDone();
    toast("Replaying Pro setup 💜", {
      style: {
        background: "#FFFFFF",
        border: "1.5px solid #5050C8",
        fontFamily: "'DM Serif Display', serif",
        color: "#1C1A3E",
      },
      duration: 1500,
    });
    setTimeout(() => {
      openProSetupFlow();
      navigate("/");
      setReplayingProSetup(false);
    }, 400);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin");
  };

  // "Add your name" / edit-profile entry point — works for any account (SSO
  // or email/password, new or existing), since it just reads/writes the same
  // first_name auth-metadata key #164's signup field uses. Needed because
  // Apple Sign In doesn't hand Kin a name today (no `name` scope requested
  // in SocialAuthButtons.tsx's signInWithOAuth call), so Apple SSO users have
  // no name at all unless they set one here.
  const startEditName = () => {
    setNameInput(currentFirstName ?? "");
    setEditingName(true);
  };

  const cancelEditName = () => {
    setEditingName(false);
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || savingName) return;
    setSavingName(true);
    try {
      const { error } = await supabase.auth.updateUser({ data: { first_name: trimmed } });
      if (error) throw error;
      setEditingName(false);
    } catch (e) {
      console.error("Name update failed:", e);
      toast.error("Couldn't save your name. Try again?");
    } finally {
      setSavingName(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeletingFlag(true); // gate global auth error handling
    try {
      // Both Supabase clients point at the same Personal Supabase project
      // post-cutover, but this call goes through the external-supabase call
      // path (raw fetch to EXTERNAL_SUPABASE_URL) rather than
      // supabase.functions.invoke, per the App Store compliance call-path
      // separation described in src/integrations/external-supabase/client.ts.
      // The delete-account Edge Function validates this session JWT itself
      // and calls the delete_user_data() RPC via a userClient built from it
      // (see supabase/functions/delete-account/index.ts) — that RPC is the
      // sole place account data actually gets deleted.
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const response = await fetch(
        `${EXTERNAL_SUPABASE_URL}/functions/v1/delete-account`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
            apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        // Failure path: surface the real error, keep session intact, allow retry.
        let msg = "Couldn't delete your account. Please try again.";
        try {
          const body = await response.json();
          if (body?.error) msg = body.error;
        } catch {
          // body wasn't JSON, fall back to default message
        }
        console.error("Account deletion failed:", response.status, msg);
        toast.error(msg);
        setDeletingFlag(false);
        setDeleting(false);
        return;
      }

      // Success path. JWT is now orphaned, do everything locally.
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch (signOutErr) {
        console.warn("Local sign-out after deletion failed (expected):", signOutErr);
      }
      clearSupabaseStorage();

      const TOAST_DURATION = 2500;
      let redirected = false;
      const goHome = () => {
        if (redirected) return;
        redirected = true;
        window.location.href = "/";
      };

      toast("Your account is gone. Take care, mama 💜", {
        style: {
          background: "#FFFFFF",
          border: "1.5px solid #5050C8",
          fontFamily: "'DM Serif Display', serif",
          color: "#1C1A3E",
        },
        duration: TOAST_DURATION,
        // Redirect exactly when the toast finishes its lifecycle.
        // Fires on auto-close, manual dismiss, and swipe.
        onAutoClose: goHome,
        onDismiss: goHome,
      });

      // Safety net: if the toast callback never fires (Toaster unmounted,
      // tab backgrounded, sonner internals change), still redirect.
      setTimeout(goHome, TOAST_DURATION + 600);
      return;
    } catch (e) {
      console.error("Account deletion error:", e);
      toast.error("Couldn't delete your account. Please try again.");
      setDeletingFlag(false);
      setDeleting(false);
    }
  };

  // Map mom type keys to their display data
  const selectedMomTypeData = (momTypes || [])
    .map((key) => MOM_TYPES.find((m) => m.key === key))
    .filter(Boolean);

  const kidNames = (prefs.known_kid_names || []).filter(
    (n) => typeof n === "string" && n.trim().length > 0
  );

  // Morning Notes "{briefingOnCount} of {BRIEFING_ADDON_KEYS.length}".
  //
  // ⚠️ Both numbers come from BriefingBuilder's own exported array now, not
  // from a list repeated here. This used to hand-list five keys next to a
  // literal "of 5", which is exactly the failure the DELIVERY_FEATURES comment
  // below warns about (#109) — and #1898's sixth add-on walked into it: six
  // toggles on would have read "5 of 5" with nothing failing. The tier rule
  // (weather free, the rest Pro) lives with the toggles too, so a lapsed Pro
  // account's saved row cannot be counted here and uncounted there.
  //
  // News is in both numbers as of Aug 19 2026: its toggle is live again (#449
  // reverted), so a saved news.on corresponds to a row she can actually find.
  const briefingPrefs = (prefs.briefing_prefs as Record<string, { on?: boolean }> | null) ?? {};
  const briefingOnCount = BRIEFING_ADDON_KEYS.filter((key) =>
    isBriefingAddonOn(briefingPrefs, key, isPro)
  ).length;

  // "How Kin reaches you" collapsed summary. Mirrors HowKinReachesYou's own
  // derivation exactly — a row is ON when EITHER of its two channel fields is
  // true — so the number here can never disagree with the toggles inside.
  const smsLive = useAppSetting(APP_SETTING_KEYS.SMS_LIVE);
  // ⚠️ Derived from DELIVERY_FEATURES, never re-listed by hand (#109). This was
  // a hardcoded array of three, and the summary line below said "of 3" — so
  // adding a fourth row in HowKinReachesYou would have left this collapsed row
  // reading "2 of 3" while three toggles were on, with nothing failing.
  const deliveryOnCount = DELIVERY_FEATURES.filter((f) => isRowOn(prefs, f)).length;

  const emailValue = !hasInboxAccess
    ? "Unlock with Pro"
    : !inboxAddress
    ? "Set it up"
    : `${inboxUsageCount ?? 0} this month`;

  return (
    <div className="h-[100dvh] flex flex-col" style={{ backgroundColor: "#F0F0FF" }}>
      {/* Header */}
      <header className="px-4 py-4 flex-shrink-0" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}>
        <div className="flex items-center max-w-2xl mx-auto w-full">
          <button
            onClick={() => navigate("/")}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/50 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M13 4L7 10L13 16" stroke="#1C1A3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="font-display text-center flex-1 mr-10" style={{ fontSize: "22px", color: "#1C1A3E" }}>
            Settings
          </h1>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 flex-col px-4 pb-10 max-w-2xl mx-auto w-full overflow-y-auto">
        {/* Kin Pro badge — renders only for Pro / admin-preview, above Account */}
        <KinProBadge />

        {/* Brand tagline */}
        <p
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 15,
            fontWeight: 500,
            color: "#5050C8",
            textAlign: "center",
            marginTop: 16,
            marginBottom: 12,
          }}
        >
          Built by a non-techy mom, for non-techy moms 💜
        </p>

        {/* Account. Not a ZoneLabel (different type scale, no emoji), but it
            gets ZoneLabel's 40-above / 8-below rhythm so every section label on
            this screen leans the same way. mt-7 rather than 40: this label's
            gap above is its own margin PLUS the tagline's 12px marginBottom. */}
        <section className="mt-7">
          <h2 className="font-body text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: "#54525C" }}>
            Account
          </h2>

          {/* Profile — name */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-shrink-0">
              <KinAvatar size="sm" variant="dark" />
              {!editingName && (
                <button
                  onClick={startEditName}
                  aria-label={currentFirstName ? "Edit your name" : "Add your name"}
                  className="absolute flex items-center justify-center rounded-full transition-opacity hover:opacity-90"
                  style={{
                    bottom: -4,
                    right: -4,
                    width: 20,
                    height: 20,
                    backgroundColor: "#5050C8",
                    border: "2px solid #F0F0FF",
                    color: "#FFFFFF",
                  }}
                >
                  <Pencil size={10} />
                </button>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    type="text"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveName();
                      if (e.key === "Escape") cancelEditName();
                    }}
                    // #552 — already has its own Enter handler; it still needs the
                    // key labelled. Names are exactly what autocorrect mangles.
                    enterKeyHint="done"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="Your first name"
                    maxLength={40}
                    disabled={savingName}
                    className="flex-1 min-w-0 font-body bg-white"
                    style={{
                      border: "1.5px solid #C8C8F4",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 14,
                      color: "#1C1A3E",
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={!nameInput.trim() || savingName}
                    aria-label="Save name"
                    className="flex items-center justify-center rounded-full flex-shrink-0 disabled:opacity-40"
                    style={{ width: 28, height: 28, color: "#5050C8" }}
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={cancelEditName}
                    disabled={savingName}
                    aria-label="Cancel"
                    className="flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ width: 28, height: 28, color: "#54525C" }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : currentFirstName ? (
                <button
                  onClick={startEditName}
                  className="font-body font-semibold flex items-center gap-1.5"
                  style={{ fontSize: 15, color: "#1C1A3E" }}
                >
                  {currentFirstName}
                  <Pencil size={11} style={{ color: "#8B8B9E" }} />
                </button>
              ) : (
                <button
                  onClick={startEditName}
                  className="font-body font-semibold hover:underline"
                  style={{ fontSize: 14, color: "#5050C8", textDecoration: "underline" }}
                >
                  Add your name
                </button>
              )}
            </div>
          </div>

          <p className="font-body text-sm mb-4" style={{ color: "#1C1A3E", wordBreak: "break-all" }}>
            {user?.email}
          </p>
          <button
            onClick={handleSignOut}
            className="w-full font-body font-semibold text-sm bg-white transition-all hover:opacity-80"
            style={{
              border: "1.5px solid #E0DEEE",
              borderRadius: 12,
              padding: "9px 0",
              fontSize: 13,
              color: "#54525C",
            }}
          >
            Sign out
          </button>
        </section>

        {/* ── Zone 0: KIN PRO ──────────────────────────────────────────────
            One place at the top of Settings for everything Pro, instead of an
            upgrade path that did not exist and a setup door buried under
            "Help & about". Hidden entirely until proLoaded, so a mom who
            already pays never sees "Upgrade" flash at her on a cold start.

            The setup row is MOVED here from Help & about, not duplicated —
            two identical doors to the same wizard on one screen would be
            worse than the one buried door it replaces. Its testId is
            deliberately unchanged so the e2e specs that drive the wizard
            through it keep working. */}
        {proLoaded && (
          <>
            <ZoneLabel>⚡ Kin Pro</ZoneLabel>
            <ZoneCard>
              {!isPro && (
                <ActionRow
                  icon="⚡"
                  label="Upgrade to Kin Pro"
                  sublabel="See what's included."
                  testId="settings-pro-upgrade-row"
                >
                  <button
                    onClick={openPro}
                    className="font-body font-semibold text-sm flex-shrink-0"
                    style={{ color: "#5050C8", textDecoration: "underline", padding: "8px 4px" }}
                  >
                    Upgrade
                  </button>
                </ActionRow>
              )}

              {/* No status row here on purpose. KinProBadge sits directly
                  above this zone on the same screen and already shows Pro
                  status, the renewal date AND a working Manage subscription
                  link — a "Kin Pro ✓ / manage it from the badge above" row
                  was pure duplication pointing at the thing next to it. This
                  zone holds actions she cannot get anywhere else. */}

              {/* Every Pro account, not just admins/test cohort. isPro here is
                  useIsPro(), which folds in the entitlement-flip override, so
                  a mom who upgraded seconds ago gets this row without a
                  refresh. Copy follows the same flag: "Replay" is wrong for
                  someone who has never seen the flow. */}
              {isPro && (
                <ActionRow
                  icon="💜"
                  label={proSetupDone ? "Replay Pro setup" : "Finish setting up Kin Pro"}
                  sublabel={
                    proSetupDone
                      ? "See the Pro welcome celebration again, anytime."
                      : "One minute, and she starts remembering your world."
                  }
                  testId="pro-setup-replay-row"
                >
                  <button
                    onClick={handleReplayProSetup}
                    disabled={replayingProSetup}
                    className="font-body font-semibold text-sm flex-shrink-0 disabled:opacity-50"
                    style={{ color: "#5050C8", textDecoration: "underline", padding: "8px 4px" }}
                  >
                    {replayingProSetup ? "Loading…" : proSetupDone ? "Replay" : "Finish"}
                  </button>
                </ActionRow>
              )}
            </ZoneCard>
          </>
        )}

        {/* Zone 1: YOU & YOUR CREW */}
        <ZoneLabel>👩 You &amp; your crew</ZoneLabel>
        <ZoneCard>
          <SummaryRow
            icon="🎀"
            label="Mom style"
            value={selectedMomTypeData.length > 0 ? `${selectedMomTypeData.length} picked` : "Add them"}
            valueColor={selectedMomTypeData.length > 0 ? "#54525C" : "#5050C8"}
            expanded={expandedRow === "momStyle"}
            onToggle={() => toggleRow("momStyle")}
          >
            {selectedMomTypeData.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedMomTypeData.map((mom) => (
                  <span
                    key={mom!.key}
                    className="inline-flex items-center font-body font-medium"
                    style={{
                      backgroundColor: "#5050C8",
                      color: "#FFFFFF",
                      borderRadius: 999,
                      padding: "6px 12px",
                      fontSize: 12,
                    }}
                  >
                    {mom!.emoji} {mom!.title}
                  </span>
                ))}
              </div>
            ) : (
              <p className="font-body text-sm italic" style={{ color: "#8B8B9E" }}>
                No preferences set yet
              </p>
            )}
            <button
              onClick={() => setShowMomSelector(true)}
              className="font-body font-semibold mt-3 hover:underline transition-all"
              style={{ fontSize: 13, color: "#5050C8" }}
            >
              Change preferences
            </button>
          </SummaryRow>

          <SummaryRow
            icon="📍"
            label="Zip code"
            value={resolveSavedZip(prefs) || "Add it"}
            valueColor={resolveSavedZip(prefs) ? "#54525C" : "#5050C8"}
            expanded={expandedRow === "zip"}
            onToggle={() => toggleRow("zip")}
          >
            <input
              id="settings-zip"
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zipInput}
              onChange={(e) => setZipInput(e.target.value.replace(/\D/g, "").slice(0, 5))}
              onBlur={handleZipBlur}
              // #552 — onBlur already commits, so Enter just needs to blur.
              // A zip is digits, never prose: autocorrect can only harm it, and
              // it is also what swallows the Enter keydown on iOS.
              onKeyDown={blurOnEnter}
              enterKeyHint="done"
              autoCorrect="off"
              spellCheck={false}
              placeholder="12345"
              className="w-full font-body bg-white"
              style={{
                border: "1.5px solid #C8C8F4",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 14,
                color: "#1C1A3E",
              }}
            />
            <p className="font-body mt-1.5" style={{ fontSize: 12, color: "#54525C" }}>
              So local ideas are actually local. Kin fills this in for you
              anywhere she needs your area. Never shared.
            </p>
          </SummaryRow>

          <SummaryRow
            icon="👦"
            label="My kids"
            value={kidNames.length > 0 ? kidNames.join(", ") : "Add them"}
            valueColor={kidNames.length > 0 ? "#54525C" : "#5050C8"}
            expanded={expandedRow === "kids"}
            onToggle={() => toggleRow("kids")}
          >
            <div className="-mt-10">
              <MyKidsSection />
            </div>
          </SummaryRow>

          <SummaryRow
            id="learned-row"
            icon="🌱"
            label="What Kin's learned"
            value={factCount !== null && factCount > 0 ? `${factCount} thing${factCount === 1 ? "" : "s"}` : "Getting started"}
            expanded={expandedRow === "learned"}
            onToggle={() => toggleRow("learned")}
            highlighted={highlightedId === "learned-row"}
          >
            <div className="-mt-10">
              <FamilyProfileSection onFactCountChange={setFactCount} targetFactId={targetFactId} />
            </div>
          </SummaryRow>
        </ZoneCard>

        {/* Zone 2: WHAT KIN SENDS YOU */}
        <ZoneLabel>☀️ What Kin sends you</ZoneLabel>
        <ZoneCard>
          {/* One delivery row. "Kin texts you" used to sit directly beneath this
              one describing the same decision in a second vocabulary; both are
              now inside HowKinReachesYou as WHAT-then-HOW. Gate is push OR sms
              rather than push alone, so a Pro text-only mom on the web app can
              still reach her send time and her number (#431 follow-up). */}
          {(pushAvailable || smsLive) && (
            <SummaryRow
              icon="🔔"
              label="How Kin reaches you"
              value={`${deliveryOnCount} of ${DELIVERY_FEATURES.length}`}
              expanded={expandedRow === "pushAlerts"}
              onToggle={() => toggleRow("pushAlerts")}
            >
              {/* -mt-3, not the -mt-10 the other rows use. HowKinReachesYou
                  opens with its own bordered card, and a 40px pull-up dragged
                  that card's top border up through the title line — the
                  subtitle then landed 1.5px INSIDE the title's line box, so
                  "2 of 3" appeared to float between two lines of text instead
                  of sitting on the title. 12px clears the row's own 16px
                  bottom padding without stacking two full paddings. */}
              <div className="-mt-3">
                <HowKinReachesYou />
              </div>
            </SummaryRow>
          )}

          <SummaryRow
            icon="🌤️"
            label="Customized Morning Notes"
            value={`${briefingOnCount} of ${BRIEFING_ADDON_KEYS.length}`}
            expanded={expandedRow === "morningNotes"}
            onToggle={() => toggleRow("morningNotes")}
          >
            <div className="-mt-6">
              <BriefingBuilder />
            </div>
          </SummaryRow>

          <SummaryRow
            icon="⭐"
            label="Favorite quick actions"
            value={favorites.length > 0 ? `${favorites.length} set` : "Pick up to 3"}
            valueColor={favorites.length > 0 ? "#54525C" : "#5050C8"}
            expanded={expandedRow === "favorites"}
            onToggle={() => toggleRow("favorites")}
          >
            {favorites.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {favorites.map((label) => {
                  const def = findChipByLabel(label);
                  return (
                    <span
                      key={label}
                      className="inline-flex items-center gap-1.5 font-body font-medium"
                      style={{
                        backgroundColor: "#F0F0FF",
                        color: "#1C1A3E",
                        borderRadius: 999,
                        padding: "6px 8px 6px 12px",
                        fontSize: 12,
                        border: "1px solid #C8C8F4",
                      }}
                    >
                      <span>{def?.emoji || "⭐"} {label}</span>
                      <button
                        onClick={() => removeFavorite(label)}
                        aria-label={`Remove ${label} from favorites`}
                        className="flex items-center justify-center rounded-full hover:bg-white"
                        style={{ width: 18, height: 18 }}
                      >
                        <X size={12} style={{ color: "#54525C" }} />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="font-body text-sm italic" style={{ color: "#8B8B9E" }}>
                Tap the star on any suggestion chip in your Chat tab to pin it here.
              </p>
            )}
            <p className="font-body italic mt-3" style={{ fontSize: 12, color: "#54525C" }}>
              Up to 3 favorites — they'll always show at the top of your chip row in Chat.
            </p>
          </SummaryRow>


        </ZoneCard>

        {/* Zone 3: CONNECTED */}
        <ZoneLabel>🔌 Connected</ZoneLabel>
        <ZoneCard>
          <SummaryRow
            icon="📅"
            label="Google Calendar"
            value={gcalChecking ? "Checking…" : gcalConnected ? "Connected ✓" : "Not connected"}
            valueColor={gcalConnected ? "#16a34a" : "#54525C"}
            expanded={expandedRow === "gcal"}
            onToggle={() => toggleRow("gcal")}
          >
            {gcalChecking ? null : gcalConnected ? (
              <button
                onClick={() => setConfirmDisconnect(true)}
                className="font-body font-semibold text-sm hover:underline"
                style={{ color: "#54525C" }}
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={gcalConnect}
                className="px-4 py-2 rounded-xl font-body font-semibold text-sm text-white"
                style={{ backgroundColor: "#5050C8" }}
              >
                Connect
              </button>
            )}
          </SummaryRow>

          <SummaryRow
            icon="🛒"
            label="Kroger"
            value={krogerChecking ? "Checking…" : krogerConnected ? "Connected ✓" : "Not connected"}
            valueColor={krogerConnected ? "#16a34a" : "#54525C"}
            expanded={expandedRow === "kroger"}
            onToggle={() => toggleRow("kroger")}
          >
            {krogerChecking ? null : krogerConnected ? (
              <div className="flex flex-col gap-3">
                {/* #742 — for a Pro account, always visible once connected,
                    never gated behind a separate expand. Opt-in, default off —
                    the toggle itself is the entire opt-in mechanism.

                    #1217 audit — Pro only. Its one consumer is the meal-plan
                    commit in run-scheduled-tasks, and meal plans are Pro, so
                    for a free account "kicks in after your next meal plan"
                    was a promise about a plan that never comes. Same call as
                    #1206 made for grocery auto-fill. Connecting Kroger itself
                    stays free, and so does Disconnect below.

                    Known edge, deliberately left: a Pro plan still in its
                    20-hour review window when she lapses can commit and load
                    her cart if she'd turned this on while paying. That is a
                    plan she generated as Pro, finishing inside its own window
                    (#1216 audit), not a free feature. */}
                {isProTier && (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-body" style={{ fontSize: 13, fontWeight: 600, color: "#1C1A3E" }}>
                      Load my Kroger cart overnight
                    </p>
                    <p className="font-body" style={{ fontSize: 11.5, color: "#54525C", marginTop: 1 }}>
                      {prefs.kroger_cart_autoload_enabled
                        ? "On — Kin loads this week's dinners and due staples once your meal plan is set."
                        : "Off — Kin won't touch your Kroger cart automatically."}
                    </p>
                  </div>
                  <Switch
                    checked={prefs.kroger_cart_autoload_enabled}
                    onCheckedChange={(checked) => void handleToggleKrogerAutoload(checked)}
                    disabled={krogerAutoloadToggling}
                    aria-label="Load my Kroger cart overnight"
                  />
                </div>
                )}
                <button
                  onClick={() => setConfirmDisconnectKroger(true)}
                  className="font-body font-semibold text-sm hover:underline text-left"
                  style={{ color: "#54525C" }}
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={() => void krogerConnect()}
                className="px-4 py-2 rounded-xl font-body font-semibold text-sm text-white"
                style={{ backgroundColor: "#5050C8" }}
              >
                Connect
              </button>
            )}
          </SummaryRow>

          {/* #1647 — ONE row, two directions. Apple has no write API, so
              Kin → Apple (a calendar Apple subscribes to, one tap) and
              Apple → Kin (her share link, pasted) are separate actions; she
              should never have to know why. Kin → Apple first: it is the one
              most people mean by "connect my calendar". */}
          <SummaryRow
            icon="🍎"
            label="Apple Calendar"
            value={appleRowValue}
            valueColor={kinFeedActive || appleCalConnected ? "#16a34a" : "#54525C"}
            expanded={expandedRow === "appleCal"}
            onToggle={() => toggleRow("appleCal")}
          >
            <KinCalendarFeedPanel feed={kinFeed} />
            <div style={{ borderTop: "1px solid #E0DEEE", margin: "12px 0" }} />
            <p className="font-body font-semibold mb-1" style={{ fontSize: 14, color: "#1C1A3E" }}>
              Let Kin read your Apple Calendar
            </p>
            <AppleCalendarConnect />
          </SummaryRow>

          {/* Holidays — the holiday layer's one setting.
              Sits with the calendar rows because that is what it changes, and
              AFTER them because it is about what the calendar DRAWS rather
              than what it is connected to.

              ⚠️ Backed by localStorage, not `user_preferences` — see
              `src/lib/showHolidaysPref.ts`. Every pref in that table is read
              through an explicit select list, so one more would be a new
              column and a migration; the brief said skip the toggle in that
              case, so this avoids the column instead. The honest cost is that
              it is per-device, which the sublabel says out loud rather than
              letting her discover it on her laptop. */}
          <SummaryRow
            icon="🎃"
            label="Holidays"
            value={showHolidays ? "Showing" : "Hidden"}
            valueColor={showHolidays ? "#16a34a" : "#54525C"}
            expanded={expandedRow === "holidays"}
            onToggle={() => toggleRow("holidays")}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-body" style={{ fontSize: 13, fontWeight: 600, color: "#1C1A3E" }}>
                  Show holidays
                </p>
                <p className="font-body" style={{ fontSize: 11.5, color: "#54525C", marginTop: 1 }}>
                  {showHolidays
                    ? "On — US holidays show on your calendar, and Kin knows about them. This setting is just for this device."
                    : "Off — no holidays on your calendar, and Kin won't bring them up. This setting is just for this device."}
                </p>
              </div>
              <Switch
                checked={showHolidays}
                onCheckedChange={(checked) => writeShowHolidays(checked)}
                aria-label="Show holidays"
              />
            </div>
          </SummaryRow>

          {/* #1073 — Pro-only to connect (checked server-side too). Still shown
              to anyone with a STORED connection — lapsed, or with a token
              Pinterest stopped honouring — so she can always remove it. */}
          {(isProTier || pinterestStored) && (
            <SummaryRow
              icon="📌"
              label="Pinterest"
              value={pinterestChecking ? "Checking…" : pinterestConnected ? "Connected ✓" : pinterestStored ? "Reconnect needed" : "Not connected"}
              valueColor={pinterestConnected ? "#16a34a" : "#54525C"}
              expanded={expandedRow === "pinterest"}
              onToggle={() => toggleRow("pinterest")}
            >
              {pinterestChecking ? null : pinterestStored ? (
                <div className="flex flex-col gap-3">
                  <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.5 }}>
                    {pinterestConnected
                      ? "Pick one of your boards under 📌 Add inspo in any party. Kin reads it when you ask and saves nothing from Pinterest."
                      : "Pinterest stopped letting Kin read your boards. Reconnect to pick a board again, or disconnect."}
                  </p>
                  {!pinterestConnected && isProTier && (
                    <button
                      onClick={() => {
                        pinterestConnect().catch((err: unknown) => {
                          toast.error(err instanceof Error ? err.message : "Couldn't start connecting Pinterest.");
                        });
                      }}
                      className="px-4 py-2 rounded-xl font-body font-semibold text-sm text-white self-start"
                      style={{ backgroundColor: "#5050C8" }}
                      data-testid="settings-pinterest-reconnect"
                    >
                      Reconnect
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmDisconnectPinterest(true)}
                    className="font-body font-semibold text-sm hover:underline text-left"
                    style={{ color: "#54525C" }}
                    data-testid="settings-pinterest-disconnect"
                  >
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.5 }}>
                    Read one of your boards as party inspo, instead of taking screenshots.
                  </p>
                  <button
                    onClick={() => {
                      pinterestConnect().catch((err: unknown) => {
                        toast.error(err instanceof Error ? err.message : "Couldn't start connecting Pinterest.");
                      });
                    }}
                    className="px-4 py-2 rounded-xl font-body font-semibold text-sm text-white flex-shrink-0"
                    style={{ backgroundColor: "#5050C8" }}
                    data-testid="settings-pinterest-connect"
                  >
                    Connect
                  </button>
                </div>
              )}
            </SummaryRow>
          )}

          <SummaryRow
            id="email-forwarding-row"
            icon="💌"
            label="Email forwarding"
            value={emailValue}
            valueColor={hasInboxAccess && inboxAddress ? "#54525C" : "#5050C8"}
            expanded={expandedRow === "email"}
            onToggle={() => (hasInboxAccess ? toggleRow("email") : setInboxProSheetOpen(true))}
            highlighted={highlightedId === "email-forwarding-row"}
          >
            {!inboxAddress ? (
              <div className="flex items-start justify-between gap-3">
                <p className="font-body text-xs" style={{ color: "#54525C", lineHeight: 1.5 }}>
                  Forward school newsletters, receipts, and travel confirmations. Kin handles each one automatically.
                </p>
                <button
                  onClick={handleSetUpInbox}
                  disabled={settingUpInbox}
                  className="px-4 py-2 rounded-xl font-body font-semibold text-sm text-white flex-shrink-0 disabled:opacity-50"
                  style={{ backgroundColor: "#5050C8" }}
                >
                  {settingUpInbox ? "…" : "Set up →"}
                </button>
              </div>
            ) : (
              <div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(inboxAddress)
                      .then(() => toast.success("Copied!"))
                      .catch(() => toast.error("Couldn't copy"));
                  }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: "#F0F0FF", border: "1.5px solid #C8C8F4", borderRadius: 12,
                    padding: "9px 13px", cursor: "pointer",
                  }}
                >
                  <p className="font-body" style={{ fontSize: 11, color: "#54525C", margin: 0, marginBottom: 2 }}>Kin's email 💌</p>
                  <p style={{ fontFamily: "monospace", fontSize: 13, color: "#5050C8", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {inboxAddress}
                  </p>
                </button>
                <p className="font-body text-xs mt-2" style={{ color: "#54525C" }}>
                  {isProTier
                    ? `You've forwarded ${inboxUsageCount ?? 0} email${inboxUsageCount === 1 ? "" : "s"} this month, making Kin work for you 💜`
                    : `${Math.min(inboxUsageCount ?? 0, FREE_INBOX_MONTHLY_CAP)} of ${FREE_INBOX_MONTHLY_CAP} imports used this month`}
                  {" · "}
                  <button
                    onClick={handleManageInbox}
                    className="font-body font-semibold hover:underline"
                    style={{ color: "#54525C" }}
                  >
                    Manage
                  </button>
                </p>
              </div>
            )}
          </SummaryRow>
        </ZoneCard>

        {/* Zone 4: HELP & ABOUT */}
        <ZoneLabel>❓ Help &amp; about</ZoneLabel>
        <ZoneCard>
          <ActionRow icon="👋" label="Welcome tour" sublabel="Take the 8-step intro again, anytime.">
            <button
              onClick={handleReplayTour}
              disabled={replayingTour}
              className="font-body font-semibold text-sm flex-shrink-0 disabled:opacity-50"
              style={{ color: "#5050C8", textDecoration: "underline", padding: "8px 4px" }}
            >
              {replayingTour ? "Loading…" : "Replay"}
            </button>
          </ActionRow>

          {/* The Pro setup row moved out of Help & about and into the Kin Pro
              zone near the top — see the note there. Everything about it is
              unchanged, testId included. */}

          <button
            onClick={() => setFaqSheetOpen(true)}
            className="w-full"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "16px 16px", background: "transparent", border: "none",
              cursor: "pointer", textAlign: "left",
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>💬</span>
            <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E", flex: 1 }}>
              Questions about Kin
            </span>
            <span
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 20, height: 20, padding: "0 6px", borderRadius: 999,
                background: "#F0F0FF", color: "#5050C8", fontSize: 11, fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {FAQ_ITEMS.length}
            </span>
          </button>

          <SummaryRow
            icon="🆘"
            label="Get help"
            expanded={expandedRow === "getHelp"}
            onToggle={() => toggleRow("getHelp")}
          >
            <GetHelp embedded />
          </SummaryRow>
        </ZoneCard>

        {/* Delete account — quiet but always reachable on the main scroll
            (Apple 5.1.1(v) compliance). Confirmation is a centered AlertDialog
            (see bottom of render) rather than an inline expanding card. */}
        <div className="text-center" style={{ marginTop: 32 }}>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="font-body font-semibold"
            style={{ fontSize: 12, color: "#C84B4B" }}
          >
            Delete my account
          </button>
        </div>

        {/* Legal */}
        <section className="mt-8">
          <div className="flex items-center justify-center gap-2">
            <a
              href="https://kinyoudoitall.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openExternal("https://kinyoudoitall.com/privacy");
              }}
              className="font-body text-sm font-semibold"
              style={{ color: "#5050C8" }}
            >
              Privacy Policy
            </a>
            <span className="font-body text-sm" style={{ color: "#C8C8F4" }}>·</span>
            <a
              href="https://kinyoudoitall.com/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openExternal("https://kinyoudoitall.com/terms-of-service/");
              }}
              className="font-body text-sm font-semibold"
              style={{ color: "#5050C8" }}
            >
              Terms of Use
            </a>
          </div>
          <p
            className="text-center font-body mt-6"
            style={{ fontSize: 11, color: "#54525C", opacity: 0.7 }}
          >
            Powered by AI · Built with{" "}
            <span aria-hidden="true">💜</span> by{" "}
            <span className="font-semibold" style={{ color: "#5050C8" }}>
              Kinyoudoitall
            </span>
          </p>
        </section>
      </div>

      {showMomSelector && (
        <MomTypeSelector
          editMode
          initialSelection={momTypes || []}
          onComplete={() => {
            setShowMomSelector(false);
            refetchMomTypes();
          }}
          onDismiss={() => setShowMomSelector(false)}
        />
      )}

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Calendar?</AlertDialogTitle>
            <AlertDialogDescription>
              Kin will no longer sync your events.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                // #1806 — disconnect() resolves false unless the server proved
                // the token row is gone; a silent false left this row reading
                // Connected ✓ again on the next resume with no word to her.
                //
                // AlertDialogAction is Radix's close button: its own click
                // handler closes the dialog unless the event is already
                // defaultPrevented, so this must run synchronously, before
                // the await, or the "give it another tap" below has nothing
                // left to tap. We close the controlled dialog ourselves, only
                // once the disconnect is proved.
                e.preventDefault();
                const gone = await gcalDisconnect();
                if (!gone) {
                  toast.error("Couldn't disconnect Google Calendar — give it another tap in a sec.");
                  return;
                }
                setConfirmDisconnect(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* #740 — separate from the Google Calendar dialog above so their
          confirm state can never cross-fire. Unlike gcalDisconnect (which
          optimistically flips connected=false even on a failed call),
          krogerDisconnect throws on a non-ok response — caught here so a
          failed delete surfaces an error and leaves the dialog open rather
          than confirming a disconnect that never actually happened. */}
      <AlertDialog open={confirmDisconnectKroger} onOpenChange={setConfirmDisconnectKroger}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Kroger?</AlertDialogTitle>
            <AlertDialogDescription>
              Kin won't be able to add anything to your Kroger cart until you reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await krogerDisconnect();
                  setConfirmDisconnectKroger(false);
                } catch (err) {
                  console.error("[Settings] Kroger disconnect failed:", err);
                  toast.error("Couldn't disconnect Kroger — try again in a sec 💜");
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* #1073 — same throw-on-failure shape as Kroger's dialog above. */}
      <AlertDialog open={confirmDisconnectPinterest} onOpenChange={setConfirmDisconnectPinterest}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Pinterest?</AlertDialogTitle>
            <AlertDialogDescription>
              Kin won't be able to read your boards until you reconnect. You can also remove Kin from the connected apps in your Pinterest settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await pinterestDisconnect();
                  setConfirmDisconnectPinterest(false);
                } catch (err) {
                  console.error("[Settings] Pinterest disconnect failed:", err);
                  toast.error("Couldn't disconnect Pinterest — try again in a sec 💜");
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: "#1C1A3E" }}>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "#54525C" }}>
              Are you sure? This will permanently delete your account and all your data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Never mind</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              disabled={deleting}
              style={{ backgroundColor: "#CC3333" }}
              className="hover:opacity-90"
            >
              {deleting ? "Deleting…" : "Yes, delete my account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {inboxAddress && (
        <EmailForwardingSheet
          isOpen={showInboxSheet}
          onClose={() => setShowInboxSheet(false)}
          address={inboxAddress}
          allowDisconnect={inboxSheetAllowDisconnect}
          onDisconnect={handleDisconnectInbox}
        />
      )}

      <BottomSheet
        open={inboxProSheetOpen}
        onClose={() => setInboxProSheetOpen(false)}
        title="⚡ Kin Pro"
        subtitle="Coming soon"
      >
        <div style={{ padding: "8px 0 24px" }}>
          <p className="font-body" style={{ fontSize: 15, color: "#1C1A3E", lineHeight: 1.5 }}>
            Email forwarding is a Kin Pro feature. Forward school newsletters, receipts, and travel confirmations — Kin handles each one automatically.
          </p>
        </div>
      </BottomSheet>

      <BottomSheet
        open={faqSheetOpen}
        onClose={() => setFaqSheetOpen(false)}
        title="Questions about Kin"
        subtitle="Tap any question 💜"
      >
        <div style={{ padding: "8px 0 24px" }}>
          <KinFAQ embedded />
        </div>
      </BottomSheet>
    </div>
  );
};

/** Small uppercase zone label above each Phase A zone card.
 *  marginTop 40 / margin-bottom 8 is deliberately lopsided (5:1). A section
 *  label belongs to the card BELOW it, and at 28/8 the two gaps read as close
 *  enough to equal that the label floated between the two cards instead. */
function ZoneLabel({ children }: { children: ReactNode }) {
  return (
    <h2
      className="font-body uppercase font-semibold mb-2"
      style={{ fontSize: "11px", letterSpacing: "0.5px", color: "#54525C", marginTop: 40 }}
    >
      {children}
    </h2>
  );
}

/** White bordered card holding a zone's summary rows, with a hairline divider
 *  auto-inserted between whichever rows actually render (some are gated). */
function ZoneCard({ children }: { children: ReactNode }) {
  const rows = Children.toArray(children).filter(isValidElement);
  return (
    <div className="bg-white shrink-0" style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, overflow: "hidden" }}>
      {rows.map((row, i) => (
        <div key={i}>
          {i > 0 && <div style={{ height: 1, background: "#E8E8F4", margin: "0 16px" }} />}
          {row}
        </div>
      ))}
    </div>
  );
}

interface SummaryRowProps {
  icon: string;
  label: string;
  value?: ReactNode;
  valueColor?: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
  id?: string;
  highlighted?: boolean;
}

/** One collapsed one-line row that expands in place to its exact existing
 *  controls. Used for every "tap to expand" row across all 4 zones. */
function SummaryRow({ icon, label, value, valueColor, expanded, onToggle, children, id, highlighted }: SummaryRowProps) {
  return (
    <div
      id={id}
      style={{
        boxShadow: highlighted ? "0 0 0 4px rgba(80,80,200,0.18)" : undefined,
        transition: "box-shadow 0.3s ease",
      }}
    >
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "16px 16px", background: "transparent", border: "none",
          cursor: "pointer", textAlign: "left",
        }}
      >
        <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
        <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E", flex: 1, minWidth: 0 }}>
          {label}
        </span>
        {value !== undefined && (
          <span
            className="font-body text-sm"
            style={{
              color: valueColor ?? "#54525C", flexShrink: 0, maxWidth: 160,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              // +6 on top of the row's gap:10 = 16px to the chevron, matching the
              // chevron's own 16px to the card edge. At a flat 10 the counter read
              // as wedged into the corner rather than as its own status indicator.
              marginRight: 6,
            }}
          >
            {value}
          </span>
        )}
        <ChevronDown
          size={16}
          color="#8B8B9E"
          style={{
            flexShrink: 0,
            transition: "transform 200ms ease",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>
      {expanded && <div style={{ padding: "0 16px 18px 16px" }}>{children}</div>}
    </div>
  );
}

interface ActionRowProps {
  icon: string;
  label: string;
  sublabel?: string;
  children: ReactNode;
  testId?: string;
}

/** A tap-to-act row (no expand) — Welcome tour / Replay Pro setup. */
function ActionRow({ icon, label, sublabel, children, testId }: ActionRowProps) {
  return (
    <div data-testid={testId} className="flex items-center justify-between gap-3" style={{ padding: 16 }}>
      <div className="flex items-center gap-2.5 min-w-0">
        <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
        <div className="min-w-0">
          <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>{label}</p>
          {sublabel && (
            <p className="font-body text-xs mt-0.5" style={{ color: "#54525C" }}>{sublabel}</p>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

export default Settings;
